import { useEffect, useMemo, useState } from "react";
import { taskService } from "@miplatform/api";
import { MiIcon, MiModal } from "@miplatform/components";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import { mdiEmailOutline } from "@mdi/js";
import { Box, Divider, Typography, useTheme } from "@mui/material";
import { EMAIL_TEMPLATE_TYPE_TO_DRAWER_CONFIG } from "../EmailForm/emailFormConstants";

export const EmailActivityPreviewModal = ({
  activity,
  details = null,
  isEmailModalOpen,
  closeEmailModal,
}) => {
  const [emailDetails, setEmailDetails] = useState(details);
  const stateManager = useComponentState();
  const isEmailLoading = stateManager.isBusy();
  const theme = useTheme();
  const { color = "grey" } =
    EMAIL_TEMPLATE_TYPE_TO_DRAWER_CONFIG[emailDetails?.emailType] || {};

  const getEmailDetails = async (activityId) => {
    stateManager.run(async () => {
      const response = await taskService.emails.getByActivityId({
        activityId,
      });
      if (response.status === HTTP_STATUSES.OK) {
        setEmailDetails(response.data);
      }
      if (response.status !== HTTP_STATUSES.OK) {
        setEmailDetails(null);
      }
    });
  };

  useEffect(() => {
    if (isEmailModalOpen && !details) {
      getEmailDetails(activity.id);
    }
  }, [isEmailModalOpen, details, activity.id]);

  const dangerouslySetInnerHTML = useMemo(
    () => ({
      __html: emailDetails?.body,
    }),
    [emailDetails?.body]
  );

  return (
    <MiModal
      title="Email Preview"
      open={isEmailModalOpen}
      closeModal={closeEmailModal}
      titleIcon={mdiEmailOutline}
      leftIconColor={theme.palette.primary.main}
      loading={isEmailLoading}
      width={612}
      titleContainerStyle={{
        backgroundColor: theme.palette[color].light,
        borderColor: theme.palette[color].dark,
      }}
      contentContainerStyle={{
        overflow: "auto",
        maxHeight: 710,
        paddingTop: 0,
        paddingBottom: 0,
      }}
    >
      {emailDetails ? (
        <>
          <Box
            display="flex"
            flexDirection="row"
            gap={2}
            flexWrap="wrap"
            alignItems="center"
          />
          <Box
            mb={1}
            sx={{ background: theme.palette.grey.light, borderRadius: 1, p: 1 }}
          >
            {emailDetails?.to?.trim() && (
              <Box display="flex" alignItems="center" gap={1}>
                <MiIcon
                  path={mdiEmailOutline}
                  size={0.7}
                  color={theme.palette.primary.main}
                />
                <Typography
                  fontWeight={600}
                  fontSize={12}
                  color="primary.main"
                  mr={0.5}
                >
                  To:
                </Typography>
                <Typography fontSize={12} color="common.black">
                  {emailDetails?.to
                    ? emailDetails.to.split(";").filter(Boolean).join(", ")
                    : null}
                </Typography>
              </Box>
            )}
            {emailDetails?.cc?.trim() && (
              <Box display="flex" alignItems="center" gap={1}>
                <MiIcon
                  path={mdiEmailOutline}
                  size={0.7}
                  color={theme.palette.secondary.main}
                />
                <Typography
                  fontWeight={600}
                  fontSize={12}
                  color="secondary.main"
                  mr={0.5}
                >
                  Cc:
                </Typography>
                <Typography fontSize={12} color="common.black">
                  {emailDetails.cc.split(";").filter(Boolean).join(", ")}
                </Typography>
              </Box>
            )}
            {emailDetails?.subject?.trim() && (
              <Box display="flex" alignItems="center" gap={1}>
                <MiIcon
                  path={mdiEmailOutline}
                  size={0.7}
                  color={theme.palette.grey.main}
                />
                <Typography
                  fontWeight={600}
                  fontSize={12}
                  color="grey.main"
                  mr={0.5}
                >
                  Subject:
                </Typography>
                <Typography fontSize={12} color="common.black">
                  {emailDetails.subject}
                </Typography>
              </Box>
            )}
          </Box>
          {emailDetails && (
            <Divider
              style={{
                marginBottom: 8,
                backgroundColor: theme.palette.grey.dark,
              }}
            />
          )}
          <Box dangerouslySetInnerHTML={dangerouslySetInnerHTML} />
        </>
      ) : (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          pb={4}
        >
          <MiIcon
            path={mdiEmailOutline}
            size={1.5}
            color={theme.palette.grey.medium}
          />
          <Typography fontWeight={600} color="grey.medium" fontSize={16} mb={1}>
            Could not find the email
          </Typography>
          <Typography color="grey.main" fontSize={13}>
            This activity is not linked to an email or the email could not be
            retrieved.
          </Typography>
        </Box>
      )}
    </MiModal>
  );
};
